import { triggerResize } from '@src/services/helpers/appHelpers';
import { loadUserInfo } from '@src/queries/users/loadUserInfo';
import { Start } from '@src/types/Start';
import { ServiceName, ServiceReference } from '@src/types/Services';
import { Login } from '@src/types/Login';
import { Browsing, Pathname } from '@src/types/Browsing';
import { queryClient, QUERY_KEYS } from '@src/query';
import { ApiTypes } from 'common';
import { setLoginSuccess } from '@src/state/loginAtoms';
import { auth } from '../config/firebaseAuth';
import { onAuthStateChanged } from 'firebase/auth';
import { isBackendSessionReady, waitForBackendSessionReady } from '@src/services/backendSession';
import {
  ANALYTICS_ACTIONS,
  ANALYTICS_CATEGORIES,
} from '@src/utils/analyticsTracking';

export default class StartService implements Start {
  private loginService?: Login;

  private browsingService?: Browsing;

  private started = false;

  constructor(servicesPromise: ServiceReference) {
    servicesPromise.then((services) => {
      this.loginService = services.get<Login>(ServiceName.Login);
      this.browsingService = services.get<Browsing>(ServiceName.Browsing);
    });
  }

  public init() {
    onAuthStateChanged(auth, async (fireuser) => {
      if (this.loginService?.loggingOut) {
        return;
      }
      if (!fireuser) {
        // Avoid hitting /api/user/me before auth state is confirmed.
        // Allow tokenized collection share flow to proceed.
        if (/\/collections\/\w+/.test(window.location.pathname) && new URLSearchParams(window.location.search).get('t')) {
          return;
        }
        // Skip redirect when the user is on the password reset flow.
        if (this.loginService?.checkPasswordRoute()) {
          return;
        }
        // Default unauthenticated entry point: send the user to the login screen.
        this.browsingService?.goToPage(Pathname.login);
        return;
      }
      // If the backend session cookie is still pending, wait for the login flow to complete.
      if (!isBackendSessionReady()) {
        await waitForBackendSessionReady();
      }
      // Avoid a duplicate /api/user/me on the login route; login flow handles it.
      if (window.location.pathname === Pathname.login) {
        return;
      }
      const res = await this.loadUser();

      if (res) {
        if ((fireuser || res.admin) && res.user && res.user.email) {
          this.loginSuccess();
        } else if (res.user?.email && !fireuser) {
          // Session badly cleaned, so we redirect to login page
          this.browsingService?.goToPage(Pathname.login);
        }
      }
    });
  }

  public loadUser() {
    return loadUserInfo().catch((e) => {
      if (e && e.type === 'maintenance') {
        return;
      }
      if (/\/collections\/\w+/.test(window.location.pathname) && new URLSearchParams(window.location.search).get('t')) {
        return;
      }
      if (this.loginService?.checkPasswordRoute()) {
        return;
      }
      this.browsingService?.goToPage(Pathname.login);
    });
  }

  public loginSuccess() {    setLoginSuccess();
    const login = /\/login/.exec(window.location.href);
    if (login) {
      this.start(Pathname.discover);
    } else {
      this.start();
    }
  }

  /**
     * Once the user is logged, we load discover
     * Note: this function should be called only once
     */
  public async start(redirect?: Pathname) {
    this.started = true;

    const userConfig = queryClient.getQueryData<ApiTypes.ResGetMe>(QUERY_KEYS.userConfig());
    const user = userConfig?.user;
    const admin = userConfig?.admin;
    if (!user) {
      return console.error("Can't start: user not loaded");
    }
    window.LOCALS.me = user;
    window.setTracking(user, admin);
    window.analytics?.trackEvent(ANALYTICS_ACTIONS.loginSuccess, ANALYTICS_CATEGORIES.login);

    // Redirect to the given page
    if (redirect) {
      this.browsingService?.goToPage(redirect || Pathname.discover);
    }
    setTimeout(() => triggerResize(), 20);
  }
}
